import React from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { GroupData } from "../../../../types/GroupData";
import { Download } from "lucide-react";

interface Props {
  groups: GroupData[];
  currentYear: number;
}

const ExcelDownloadButton: React.FC<Props> = ({ groups, currentYear }) => {
  const exportExcel = () => {
    const now = new Date();

    const yyyy = now.getFullYear();

    const mm = String(now.getMonth() + 1).padStart(2, "0");

    const dd = String(now.getDate()).padStart(2, "0");

    const fileName = `${yyyy}jbc_entry_${mm}${dd}.xlsx`;

    // Timestamp変換
    const formatDate = (timestamp: any) => {
      if (!timestamp?.toDate) return "";

      return timestamp.toDate().toLocaleString("ja-JP");
    };

    // 年度フィルタ
    const currentYearGroups = groups.filter((group) => {
      const createdAt = group.createdAt?.toDate();

      return createdAt?.getFullYear() === currentYear;
    });

    // エントリー順
    const sortByEntryNumber = (data: GroupData[]) =>
      [...data].sort(
        (a, b) => Number(a.entryNumber ?? 0) - Number(b.entryNumber ?? 0),
      );

    // Quartet Contest
    const quartetContest = sortByEntryNumber(
      currentYearGroups.filter((g) => g.entryClass === "QuartetContest"),
    );

    const quartetData = quartetContest.map((group) => ({
      エントリー番号: group.entryNumber,
      グループ名: group.groupName,
      グループ名フリガナ: group.groupNameRuby,
      代表者名: group.picName,
      代表者メール: group.picMail,
      テナー: group.quartetContestMemberTenor,
      リード: group.quartetContestMemberLead,
      バリトン: group.quartetContestMemberBaritone,
      ベース: group.quartetContestMemberBass,
      Championship: group.joinChampionship,
      Preliminary: group.joinPreliminary,
      備考: group.information,
      曲1: group.songName1,
      曲1_作詞: group.songLyrics1,
      曲1_作曲: group.songComposed1,
      曲1_アレンジ: group.songArranged1,
      曲2: group.songName2,
      曲2_作詞: group.songLyrics2,
      曲2_作曲: group.songComposed2,
      曲2_アレンジ: group.songArranged2,
      曲3: group.songName3,
      曲3_作詞: group.songLyrics3,
      曲3_作曲: group.songComposed3,
      曲3_アレンジ: group.songArranged3,
      曲4: group.songName4,
      曲4_作詞: group.songLyrics4,
      曲4_作曲: group.songComposed4,
      曲4_アレンジ: group.songArranged4,
      作成日時: formatDate(group.createdAt),
      更新日時: formatDate(group.updatedAt),
    }));

    // Chorus Contest
    const chorusContest = sortByEntryNumber(
      currentYearGroups.filter((g) => g.entryClass === "ChorusContest"),
    );

    const chorusData = chorusContest.map((group) => ({
      エントリー番号: group.entryNumber,
      グループ名: group.groupName,
      グループ名フリガナ: group.groupNameRuby,
      代表者名: group.picName,
      代表者メール: group.picMail,
      人数: group.chorusContestAttendance,
      指揮者名: group.chorusDirector,
      Championship: group.joinChampionship,
      Preliminary: group.joinPreliminary,
      曲1: group.songName1,
      曲1_作詞: group.songLyrics1,
      曲1_作曲: group.songComposed1,
      曲1_アレンジ: group.songArranged1,
      曲2: group.songName2,
      曲2_作詞: group.songLyrics2,
      曲2_作曲: group.songComposed2,
      曲2_アレンジ: group.songArranged2,
      備考: group.information,
      作成日時: formatDate(group.createdAt),
      更新日時: formatDate(group.updatedAt),
    }));

    // Evaluation Only
    const evaluationOnly = sortByEntryNumber(
      currentYearGroups.filter((g) => g.entryClass === "EvaluationOnly"),
    );

    const evaluationData = evaluationOnly.map((group) => ({
      エントリー番号: group.entryNumber,
      グループ名: group.groupName,
      グループ名フリガナ: group.groupNameRuby,
      代表者名: group.picName,
      代表者メール: group.picMail,
      人数: group.chorusContestAttendance,
      指揮者名: group.chorusDirector,
      曲1: group.songName1,
      曲1_作詞: group.songLyrics1,
      曲1_作曲: group.songComposed1,
      曲1_アレンジ: group.songArranged1,
      曲2: group.songName2,
      曲2_作詞: group.songLyrics2,
      曲2_作曲: group.songComposed2,
      曲2_アレンジ: group.songArranged2,
      備考: group.information,
      作成日時: formatDate(group.createdAt),
      更新日時: formatDate(group.updatedAt),
    }));

    // workbook生成
    const workbook = XLSX.utils.book_new();

    // Quartet Contest
    const quartetSheet = XLSX.utils.json_to_sheet(quartetData);

    XLSX.utils.book_append_sheet(workbook, quartetSheet, "Quartet Contest");

    // Chorus Contest
    const chorusSheet = XLSX.utils.json_to_sheet(chorusData);

    XLSX.utils.book_append_sheet(workbook, chorusSheet, "Chorus Contest");

    // Evaluation Only
    const evaluationSheet = XLSX.utils.json_to_sheet(evaluationData);

    XLSX.utils.book_append_sheet(workbook, evaluationSheet, "Evaluation Only");

    // Excel生成
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // ダウンロード
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, fileName);
  };

  return (
    <button onClick={exportExcel} className="download-btn excel">
      <Download size={16} />
    </button>
  );
};

export default ExcelDownloadButton;
